<script setup lang="ts">
import axios, { isAxiosError } from 'axios'
import { FetchError } from 'ofetch'
import { VForm } from 'vuetify/components/VForm'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import type { App } from '@/types/generated'
import { Modules } from '@/types/generated'
import { languageTitle } from '@/utils/languageTitle'

type CatalogItem = App.Http.Controllers.TtsVoice.TtsVoiceCatalogItemData
type Gender = Modules.TtsVoice.Enums.TtsVoiceGenderEnum
type Language = Modules.Shared.Enums.LanguageEnum
type Provider = Modules.AIConnector.Enums.ProviderEnum
type FieldErrors = Partial<Record<string, string[]>>

interface OrganizationOption {
  id: string
  title: string
}

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()

const MAX_SAMPLE_BYTES = 5 * 1024 * 1024

const SAMPLE_ACCEPT = '.mp3,.mp4,.m4a,audio/mpeg,audio/mp4'

// VSelect treats a null model as "nothing selected", so shared visibility needs its own value.
const ALL_ORGANIZATIONS = 'all'

const VOICE_FIELDS = ['name', 'provider', 'externalId', 'gender', 'languages', 'speed', 'organizationId', 'sample']

const PROVIDER_TITLES: Partial<Record<Provider, string>> = {
  [Modules.AIConnector.Enums.ProviderEnum.FISHAUDIO]: 'Fish Audio',
}

const { data: voicesData, execute: fetchVoices, isFetching } = await useApi<CatalogItem[]>('/tts-voices')
const { data: organizationsData } = await useApi<OrganizationOption[]>('/organizations/all')

const organizations = computed<OrganizationOption[]>(() => organizationsData.value ?? [])

const organizationTitle = (organizationId: string | null): string => {
  if (organizationId === null)
    return t('ttsVoices.visibility.all')

  return organizations.value.find(organization => organization.id === organizationId)?.title ?? organizationId
}

const providerTitle = (provider: Provider): string => PROVIDER_TITLES[provider] ?? provider

const rows = computed(() => (voicesData.value ?? []).map(voice => ({
  ...voice,
  providerTitle: providerTitle(voice.provider),
  languagesTitle: Object.values(voice.languages).map(languageTitle).join(', '),
  visibilityTitle: organizationTitle(voice.organizationId),
})))

const searchQuery = ref('')

const headers = [
  { title: t('ttsVoices.headers.name'), key: 'name' },
  { title: t('ttsVoices.headers.provider'), key: 'providerTitle' },
  { title: t('ttsVoices.headers.externalId'), key: 'externalId' },
  { title: t('ttsVoices.headers.gender'), key: 'gender' },
  { title: t('ttsVoices.headers.languages'), key: 'languagesTitle' },
  { title: t('ttsVoices.headers.visibility'), key: 'visibilityTitle' },
  { title: t('ttsVoices.headers.sample'), key: 'sampleUrl', sortable: false },
  { title: t('ttsVoices.headers.profiles'), key: 'profilesCount' },
  { title: '', key: 'actions', sortable: false },
]

const providerOptions = Object.keys(PROVIDER_TITLES)
  .map(provider => ({ value: provider, title: providerTitle(provider as Provider) }))

const genderOptions = Object.values(Modules.TtsVoice.Enums.TtsVoiceGenderEnum)
  .map(gender => ({ value: gender, title: t(`ttsVoices.gender.${gender}`) }))

const languageOptions = Object.values(Modules.Shared.Enums.LanguageEnum)
  .map(language => ({ value: language, title: languageTitle(language) }))

const visibilityOptions = computed(() => [
  { value: ALL_ORGANIZATIONS, title: t('ttsVoices.visibility.all') },
  ...organizations.value.map(organization => ({ value: organization.id, title: organization.title })),
])

const toOrganizationId = (visibility: string): string | null => visibility === ALL_ORGANIZATIONS ? null : visibility

const toVisibility = (organizationId: string | null): string => organizationId ?? ALL_ORGANIZATIONS

const requestFailedMessage = () => t('ttsVoices.errors.requestFailed')

const toFieldErrors = (error: unknown): FieldErrors => {
  let status: number | undefined
  let errors: unknown

  if (isAxiosError(error)) {
    status = error.response?.status
    errors = error.response?.data?.errors
  }
  else if (error instanceof FetchError) {
    status = error.statusCode
    errors = error.data?.errors
  }

  if (status !== 422 || !errors || typeof errors !== 'object')
    return { '': [requestFailedMessage()] }

  const fieldErrors: FieldErrors = {}

  for (const [key, messages] of Object.entries(errors)) {
    const field = key.split('.')[0]

    fieldErrors[field] = [...(fieldErrors[field] ?? []), ...[messages].flat().map(String)]
  }

  return fieldErrors
}

const otherErrors = (errors: FieldErrors): string[] => Object.entries(errors)
  .filter(([field]) => !VOICE_FIELDS.includes(field))
  .flatMap(([, messages]) => messages ?? [])

const sampleSizeRule = (files: File[]) => !files?.length || files[0].size <= MAX_SAMPLE_BYTES || t('ttsVoices.validation.sampleTooLarge')

const createSampleRules = [sampleSizeRule]

const replaceSampleRules = [requiredValidator, sampleSizeRule]

const pageError = ref('')
const isPageErrorVisible = ref(false)

const showPageError = (message: string) => {
  pageError.value = message
  isPageErrorVisible.value = true
}

const sampleAudio = new Audio()
const playingVoiceId = ref<string | null>(null)

const stopSample = () => {
  sampleAudio.pause()
  playingVoiceId.value = null
}

const failSample = (voiceId: string | null) => {
  if (voiceId === null || playingVoiceId.value !== voiceId)
    return

  stopSample()
  showPageError(t('ttsVoices.errors.sampleUnavailable'))
}

sampleAudio.addEventListener('ended', () => {
  playingVoiceId.value = null
})
sampleAudio.addEventListener('error', () => failSample(playingVoiceId.value))

const toggleSample = async (voice: CatalogItem) => {
  if (playingVoiceId.value === voice.id) {
    stopSample()

    return
  }

  sampleAudio.pause()
  sampleAudio.src = voice.sampleUrl
  playingVoiceId.value = voice.id

  try {
    await sampleAudio.play()
  }
  catch {
    failSample(voice.id)
  }
}

onBeforeUnmount(() => {
  stopSample()
  sampleAudio.removeAttribute('src')
  sampleAudio.load()
})

const isVoiceDialogVisible = ref(false)
const isDetachConfirmVisible = ref(false)
const isSpeedPromptVisible = ref(false)
const isSavingAndSynthesizing = ref(false)
const voiceTarget = ref<CatalogItem | null>(null)
const voiceForm = ref<VForm>()
const voiceErrors = ref<FieldErrors>({})
const isSaving = ref(false)
const detachProfilesCount = ref(0)
const sampleFiles = ref<File[]>([])

const emptyVoiceData = () => ({
  name: '',
  provider: Modules.AIConnector.Enums.ProviderEnum.FISHAUDIO,
  externalId: '',
  gender: Modules.TtsVoice.Enums.TtsVoiceGenderEnum.Male as Gender,
  languages: [] as Language[],
  speed: 1,
  visibility: ALL_ORGANIZATIONS,
})

const voiceData = ref(emptyVoiceData())

// VSlider emits keyboard steps unrounded: 0.7 + 0.1 gives 0.7999999999999999.
const roundSpeed = (speed: number): number => Number(speed.toFixed(1))

const isSpeedChanged = computed(() => voiceTarget.value !== null && voiceData.value.speed !== voiceTarget.value.speed)

const openCreateDialog = () => {
  voiceTarget.value = null
  voiceData.value = emptyVoiceData()
  sampleFiles.value = []
  voiceErrors.value = {}
  isVoiceDialogVisible.value = true
}

const openEditDialog = (voice: CatalogItem) => {
  voiceTarget.value = voice
  voiceData.value = {
    name: voice.name,
    provider: voice.provider,
    externalId: voice.externalId,
    gender: voice.gender,
    languages: Object.values(voice.languages),
    speed: voice.speed,
    visibility: toVisibility(voice.organizationId),
  }
  voiceErrors.value = {}
  isVoiceDialogVisible.value = true
}

const createVoice = async () => {
  const formData = new FormData()

  formData.append('name', voiceData.value.name)
  formData.append('provider', voiceData.value.provider)
  formData.append('externalId', voiceData.value.externalId)
  formData.append('gender', voiceData.value.gender)
  voiceData.value.languages.forEach(language => formData.append('languages[]', language))
  formData.append('speed', String(voiceData.value.speed))
  if (sampleFiles.value.length)
    formData.append('sample', sampleFiles.value[0])

  const organizationId = toOrganizationId(voiceData.value.visibility)
  if (organizationId !== null)
    formData.append('organizationId', organizationId)

  isSaving.value = true
  voiceErrors.value = {}
  try {
    await axios.post('/api/tts-voices', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    isVoiceDialogVisible.value = false
    await fetchVoices()
  }
  catch (error) {
    voiceErrors.value = toFieldErrors(error)
  }
  finally {
    isSaving.value = false
  }
}

const saveEdit = async (): Promise<boolean> => {
  const voice = voiceTarget.value
  if (voice === null)
    return false

  const body: App.Http.Controllers.TtsVoice.UpdateTtsVoiceData = {
    name: voiceData.value.name,
    provider: voiceData.value.provider,
    externalId: voiceData.value.externalId,
    gender: voiceData.value.gender,
    languages: voiceData.value.languages,
    speed: voiceData.value.speed,
    organizationId: toOrganizationId(voiceData.value.visibility),
  }

  isSaving.value = true
  voiceErrors.value = {}
  try {
    await $api(`/tts-voices/${voice.id}`, { method: 'PUT', body })
    isVoiceDialogVisible.value = false
    await fetchVoices()

    return true
  }
  catch (error) {
    voiceErrors.value = toFieldErrors(error)

    return false
  }
  finally {
    isSaving.value = false
  }
}

const confirmSaveEdit = async () => {
  if (isSpeedChanged.value)
    isSpeedPromptVisible.value = true
  else
    await saveEdit()
}

const saveOnly = async () => {
  isSpeedPromptVisible.value = false
  await saveEdit()
}

const submitEdit = async (voice: CatalogItem) => {
  const newOrganizationId = toOrganizationId(voiceData.value.visibility)
  const narrowsVisibility = newOrganizationId !== null && newOrganizationId !== voice.organizationId
  const changesGender = voiceData.value.gender !== voice.gender

  if (!narrowsVisibility && !changesGender) {
    await confirmSaveEdit()

    return
  }

  const query: Record<string, string> = {}
  if (narrowsVisibility)
    query.organizationId = newOrganizationId
  if (changesGender)
    query.gender = voiceData.value.gender

  isSaving.value = true
  voiceErrors.value = {}
  try {
    const preview = await $api<{ profiles: number }>(`/tts-voices/${voice.id}/detach-preview`, { query })

    detachProfilesCount.value = preview.profiles
  }
  catch (error) {
    voiceErrors.value = toFieldErrors(error)

    return
  }
  finally {
    isSaving.value = false
  }

  if (detachProfilesCount.value > 0)
    isDetachConfirmVisible.value = true
  else
    await confirmSaveEdit()
}

const submitVoice = async () => {
  const { valid } = await voiceForm.value!.validate()
  if (!valid)
    return

  if (voiceTarget.value === null)
    await createVoice()
  else
    await submitEdit(voiceTarget.value)
}

const isSampleDialogVisible = ref(false)
const sampleTarget = ref<CatalogItem | null>(null)
const sampleForm = ref<VForm>()
const replacementFiles = ref<File[]>([])
const sampleErrors = ref<FieldErrors>({})
const isUploadingSample = ref(false)
const isSynthesizingSample = ref(false)
const isSampleBusy = computed(() => isUploadingSample.value || isSynthesizingSample.value)

const openSampleDialog = (voice: CatalogItem) => {
  sampleTarget.value = voice
  replacementFiles.value = []
  sampleErrors.value = {}
  isSampleDialogVisible.value = true
}

const submitSample = async () => {
  const voice = sampleTarget.value
  if (voice === null)
    return

  const { valid } = await sampleForm.value!.validate()
  if (!valid)
    return

  const formData = new FormData()

  formData.append('sample', replacementFiles.value[0])

  isUploadingSample.value = true
  sampleErrors.value = {}
  try {
    await axios.post(`/api/tts-voices/${voice.id}/sample`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    if (playingVoiceId.value === voice.id)
      stopSample()
    isSampleDialogVisible.value = false
    await fetchVoices()
  }
  catch (error) {
    sampleErrors.value = toFieldErrors(error)
  }
  finally {
    isUploadingSample.value = false
  }
}

const requestSampleSynthesis = async (voiceId: string) => {
  await axios.post(`/api/tts-voices/${voiceId}/sample/synthesize`)
  if (playingVoiceId.value === voiceId)
    stopSample()
}

const synthesizeSample = async () => {
  const voice = sampleTarget.value
  if (voice === null || isSampleBusy.value)
    return

  isSynthesizingSample.value = true
  sampleErrors.value = {}
  try {
    await requestSampleSynthesis(voice.id)
    isSampleDialogVisible.value = false
    await fetchVoices()
  }
  catch (error) {
    sampleErrors.value = toFieldErrors(error)
  }
  finally {
    isSynthesizingSample.value = false
  }
}

const saveAndSynthesize = async () => {
  const voice = voiceTarget.value
  if (voice === null || isSavingAndSynthesizing.value)
    return

  isSavingAndSynthesizing.value = true
  if (await saveEdit()) {
    try {
      await requestSampleSynthesis(voice.id)
    }
    catch (error) {
      showPageError(toFieldErrors(error).sample?.[0] ?? requestFailedMessage())
    }
    await fetchVoices()
  }
  isSavingAndSynthesizing.value = false
  isSpeedPromptVisible.value = false
}

const isDeleteConfirmVisible = ref(false)
const deleteTarget = ref<CatalogItem | null>(null)

const deleteQuestion = computed(() => {
  const voice = deleteTarget.value
  if (voice === null)
    return ''

  return voice.profilesCount > 0
    ? t('ttsVoices.confirmDeleteWithProfiles', { name: voice.name, count: voice.profilesCount })
    : t('ttsVoices.confirmDelete', { name: voice.name })
})

const openDeleteConfirm = (voice: CatalogItem) => {
  deleteTarget.value = voice
  isDeleteConfirmVisible.value = true
}

const deleteVoice = async () => {
  const voice = deleteTarget.value
  if (voice === null)
    return

  if (playingVoiceId.value === voice.id)
    stopSample()

  try {
    await $api(`/tts-voices/${voice.id}`, { method: 'DELETE' })
  }
  catch {
    showPageError(requestFailedMessage())
  }
  await fetchVoices()
}
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />
        <VBtn
          prepend-icon="tabler-plus"
          @click="openCreateDialog"
        >
          {{ t('ttsVoices.create') }}
        </VBtn>
      </VCardText>

      <VDataTable
        :headers="headers"
        :items="rows"
        :search="searchQuery ?? ''"
        :loading="isFetching"
        :loading-text="t('loading')"
        :no-data-text="t('ttsVoices.empty')"
        :items-per-page="-1"
        item-value="id"
        class="text-no-wrap"
      >
        <template #item.gender="{ item }">
          {{ t(`ttsVoices.gender.${item.gender}`) }}
        </template>
        <template #item.sampleUrl="{ item }">
          <IconBtn
            :title="playingVoiceId === item.id ? t('ttsVoices.stop') : t('ttsVoices.play')"
            @click="toggleSample(item)"
          >
            <VIcon :icon="playingVoiceId === item.id ? 'tabler-player-stop' : 'tabler-player-play'" />
          </IconBtn>
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn
              :title="t('ttsVoices.replaceSample')"
              @click="openSampleDialog(item)"
            >
              <VIcon icon="tabler-upload" />
            </IconBtn>
            <IconBtn
              :title="t('ttsVoices.edit')"
              @click="openEditDialog(item)"
            >
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn
              :title="t('ttsVoices.delete')"
              @click="openDeleteConfirm(item)"
            >
              <VIcon icon="tabler-trash" />
            </IconBtn>
          </div>
        </template>
        <template #bottom />
      </VDataTable>
    </VCard>

    <VDialog
      v-model="isVoiceDialogVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 680"
      :persistent="isSaving"
    >
      <DialogCloseBtn
        :disabled="isSaving"
        @click="isVoiceDialogVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ voiceTarget ? t('ttsVoices.edit') : t('ttsVoices.create') }}
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <VForm
            ref="voiceForm"
            :disabled="isSaving"
            @submit.prevent="submitVoice"
          >
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="voiceData.name"
                  :label="t('ttsVoices.headers.name')"
                  :rules="[requiredValidator]"
                  :error-messages="voiceErrors.name"
                  maxlength="255"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="voiceData.provider"
                  :items="providerOptions"
                  :label="t('ttsVoices.headers.provider')"
                  :rules="[requiredValidator]"
                  :error-messages="voiceErrors.provider"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppTextField
                  v-model="voiceData.externalId"
                  :label="t('ttsVoices.headers.externalId')"
                  :rules="[requiredValidator]"
                  :error-messages="voiceErrors.externalId"
                  maxlength="255"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="voiceData.gender"
                  :items="genderOptions"
                  :label="t('ttsVoices.headers.gender')"
                  :error-messages="voiceErrors.gender"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="voiceData.visibility"
                  :items="visibilityOptions"
                  :label="t('ttsVoices.headers.visibility')"
                  :error-messages="voiceErrors.organizationId"
                />
              </VCol>
              <VCol cols="12">
                <AppSelect
                  v-model="voiceData.languages"
                  :items="languageOptions"
                  :label="t('ttsVoices.headers.languages')"
                  :rules="[requiredValidator]"
                  :error-messages="voiceErrors.languages"
                  chips
                  closable-chips
                  multiple
                />
              </VCol>
              <VCol cols="12">
                <VSlider
                  :model-value="voiceData.speed"
                  :label="t('ttsVoices.fields.speed')"
                  min="0.5"
                  max="2"
                  step="0.1"
                  thumb-label
                  :hint="isSpeedChanged ? t('ttsVoices.speedChangedHint') : undefined"
                  persistent-hint
                  :error-messages="voiceErrors.speed"
                  :disabled="isSaving"
                  @update:model-value="voiceData.speed = roundSpeed($event)"
                />
              </VCol>
              <VCol
                v-if="!voiceTarget"
                cols="12"
              >
                <VFileInput
                  v-model="sampleFiles"
                  :rules="createSampleRules"
                  :accept="SAMPLE_ACCEPT"
                  :label="t('ttsVoices.fields.sample')"
                  :hint="t('ttsVoices.sampleOptionalHint')"
                  :error-messages="voiceErrors.sample"
                  persistent-hint
                  prepend-icon="tabler-music"
                  @update:model-value="voiceErrors.sample = undefined"
                />
              </VCol>
              <VCol
                v-if="otherErrors(voiceErrors).length"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                >
                  <div
                    v-for="(message, index) in otherErrors(voiceErrors)"
                    :key="index"
                  >
                    {{ message }}
                  </div>
                </VAlert>
              </VCol>
              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <VBtn
                  type="submit"
                  :loading="isSaving"
                  :disabled="isSaving"
                >
                  {{ t('save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  :disabled="isSaving"
                  @click="isVoiceDialogVisible = false"
                >
                  {{ t('cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <VDialog
      v-model="isSampleDialogVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 580"
      :persistent="isSampleBusy"
    >
      <DialogCloseBtn
        :disabled="isSampleBusy"
        @click="isSampleDialogVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ t('ttsVoices.replaceSample') }}
          </VCardTitle>
          <VCardSubtitle v-if="sampleTarget">
            {{ t('ttsVoices.replaceSampleHint', { name: sampleTarget.name, version: sampleTarget.sampleVersion }) }}
          </VCardSubtitle>
        </VCardItem>

        <VCardText>
          <VForm
            ref="sampleForm"
            :disabled="isSampleBusy"
            @submit.prevent="submitSample"
          >
            <VRow>
              <VCol cols="12">
                <VFileInput
                  v-model="replacementFiles"
                  :rules="replaceSampleRules"
                  :accept="SAMPLE_ACCEPT"
                  :label="t('ttsVoices.fields.sample')"
                  :hint="t('ttsVoices.sampleHint')"
                  :error-messages="sampleErrors.sample"
                  persistent-hint
                  prepend-icon="tabler-music"
                  @update:model-value="sampleErrors = {}"
                />
              </VCol>
              <VCol
                v-if="otherErrors(sampleErrors).length"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                >
                  <div
                    v-for="(message, index) in otherErrors(sampleErrors)"
                    :key="index"
                  >
                    {{ message }}
                  </div>
                </VAlert>
              </VCol>
              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <VBtn
                  type="submit"
                  :loading="isUploadingSample"
                  :disabled="isSampleBusy"
                >
                  {{ t('save') }}
                </VBtn>
                <VBtn
                  variant="tonal"
                  :loading="isSynthesizingSample"
                  :disabled="isSampleBusy"
                  @click="synthesizeSample"
                >
                  {{ t('ttsVoices.synthesizeSample') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  :disabled="isSampleBusy"
                  @click="isSampleDialogVisible = false"
                >
                  {{ t('cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model:isDialogVisible="isDetachConfirmVisible"
      :confirmation-question="t('ttsVoices.confirmDetach', { count: detachProfilesCount })"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="confirmSaveEdit"
    />

    <VDialog
      v-model="isSpeedPromptVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 580"
      :persistent="isSavingAndSynthesizing"
    >
      <DialogCloseBtn
        :disabled="isSavingAndSynthesizing"
        @click="isSpeedPromptVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ t('ttsVoices.speedSamplePromptTitle') }}
          </VCardTitle>
        </VCardItem>

        <VCardText class="text-center">
          {{ t('ttsVoices.speedSamplePrompt') }}
        </VCardText>

        <VCardText class="d-flex flex-wrap justify-center gap-4">
          <VBtn
            :loading="isSavingAndSynthesizing"
            :disabled="isSavingAndSynthesizing"
            @click="saveAndSynthesize"
          >
            {{ t('ttsVoices.saveAndSynthesize') }}
          </VBtn>
          <VBtn
            variant="tonal"
            :disabled="isSavingAndSynthesizing"
            @click="saveOnly"
          >
            {{ t('ttsVoices.saveOnly') }}
          </VBtn>
          <VBtn
            color="secondary"
            variant="tonal"
            :disabled="isSavingAndSynthesizing"
            @click="isSpeedPromptVisible = false"
          >
            {{ t('cancel') }}
          </VBtn>
        </VCardText>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmVisible"
      :confirmation-question="deleteQuestion"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="deleteVoice"
    />

    <VSnackbar
      v-model="isPageErrorVisible"
      color="error"
    >
      {{ pageError }}
    </VSnackbar>
  </div>
</template>
